import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  LayoutDashboard,
  Calendar,
  Store,
  TrendingUp,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { logout } from '../../redux/slice/authSlice';
import { DashboardNavbar } from '../navbar';
import BrandLogo from '../brand/BrandLogo';
import styles from './dashboardLayout.module.css';

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/bookings', label: 'Bookings', icon: Calendar },
  { path: '/shops', label: 'Shops & Services', icon: Store },
  { path: '/analytics', label: 'Analytics', icon: TrendingUp },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window === 'undefined' || window.innerWidth > 1024
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const onChange = (event) => {
      setSidebarOpen(!event.matches);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const overlay = window.matchMedia('(max-width: 1024px)').matches;
    document.body.style.overflow = sidebarOpen && overlay ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    dispatch(logout());
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const isNavItemActive = (itemPath) => {
    const p = location.pathname;
    return p === itemPath || (itemPath !== '/dashboard' && p.startsWith(`${itemPath}/`));
  };

  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : 'V';

  return (
    <div className={styles.dashboardContainer}>
      {sidebarOpen ? (
        <button
          type="button"
          className={styles.sidebarBackdrop}
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside
        className={`${styles.sidebar} ${!sidebarOpen ? styles.sidebarCollapsed : ''} ${
          sidebarOpen ? styles.sidebarOpen : ''
        }`}
      >
        {/* Sidebar Header */}
        <div className={styles.sidebarHeader}>
          <a
            href="/dashboard"
            className={styles.sidebarBrand}
            aria-label="B4BARBER vendor portal"
            onClick={(e) => {
              e.preventDefault();
              navigate('/dashboard');
            }}
          >
            <BrandLogo variant={sidebarOpen ? 'lockup' : 'mark'} tone="onDark" />
            {sidebarOpen && <span className={styles.portalBadge}>Partner</span>}
          </a>
          <button
            onClick={toggleSidebar}
            className={styles.sidebarToggle}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className={styles.navigation}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isNavItemActive(item.path);
            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.path);
                  if (window.innerWidth <= 1024) setSidebarOpen(false);
                }}
                className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <div
                  className={`${styles.navIconContainer} ${
                    !sidebarOpen ? styles.navIconContainerCollapsed : ''
                  }`}
                >
                  <Icon size={16} strokeWidth={active ? 2.3 : 1.9} />
                </div>
                <span
                  className={`${styles.navLabel} ${!sidebarOpen ? styles.navLabelCollapsed : ''}`}
                >
                  {item.label}
                </span>
              </a>
            );
          })}
        </nav>

        {/* User Card & Logout Section */}
        <div className={styles.userSection}>
          {sidebarOpen ? (
            <div className={styles.userCard}>
              <div className={styles.userAvatar}>{userInitial}</div>
              <div className={styles.userDetails}>
                <div className={styles.userName}>{user?.name || 'Vendor Partner'}</div>
                <div className={styles.userRole}>Salon Owner</div>
              </div>
            </div>
          ) : null}

          <button
            onClick={handleLogout}
            className={`${styles.logoutButton} ${!sidebarOpen ? styles.logoutButtonCollapsed : ''}`}
            title="Sign out"
            aria-label="Logout"
          >
            <LogOut size={15} />
            <span
              className={`${styles.logoutLabel} ${!sidebarOpen ? styles.logoutLabelCollapsed : ''}`}
            >
              Sign out
            </span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`${styles.mainContent} ${!sidebarOpen ? styles.mainContentCollapsed : ''}`}>
        <DashboardNavbar onMenuClick={toggleSidebar} menuOpen={sidebarOpen} />

        {/* Page Content */}
        <div className={styles.contentWrapper}>{children}</div>
      </div>
    </div>
  );
};

export default DashboardLayout;
